import {
  DehydratedState,
  HydrationBoundary,
  QueryClientProvider,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import {
  getBrokerDetails,
  getTalentDetails,
  getEventTicketDetails,
  getVenueDetails,
  getEventSeriesTicketDetails,
  getQuestions,
} from '@apiCalls';
import {
  CtaContainer,
  EventMeta,
  EventWrapper,
  TalentImageRow,
} from '@components';
import { DATE_FORMAT_LONG, DATE_FORMAT_SHORT } from '@constants';
import { useAppContext } from '@contexts';
import { currencyLookup, timeFormatter, emojiParser } from '@utils';
import { handleRenderError, withErrorBoundary } from '@utils/errorHandler';
import { loadServerSideData } from '@utils/loadServerSideData';
import tracker, { InitTrackingParameters } from '@utils/tracking';
import { sharedQueryClient } from '../../_app';

export function EventLandingPage({
  event: eventProperty,
  events: eventsProperty,
  eventSeries: eventSeriesProperty,
}: {
  event?: IEvent;
  events?: IEvent[];
  eventSeries?: IEventSeries;
}) {
  const {
    push,
    query: { repId },
  } = useRouter();
  const {
    event,
    eventSeries,
    resetContext,
    setEvent,
    setEventQuestions,
    setEventSeries,
  } = useAppContext();
  // TEMPORARY: Query all events in series to check aggregate availability
  // This is needed to show "Get Tickets" if ANY event in the series has tickets
  // Example on Staging: Event 10459 has no tickets, but event 10455 (same series) does
  // Without this, event 10459 would incorrectly show "Join Waiting List"
  // TODO: Replace with lean /EventSeries/{id}/TicketAvailability endpoint
  // Query refetch settings made this safer for temporary use
  const ticketQueries: {
    queryKey: string[];
    queryFn: () => Promise<ITicket[] | ITicketSummary[] | undefined>;
    enabled?: boolean;
  }[] =
    eventsProperty
      ?.filter(event => event.ID > 0)
      .map(event => ({
        queryKey: ['tickets', String(event.ID)],
        queryFn: () => getEventTicketDetails(event.ID),
        enabled: event.ID > 0,
      })) ?? [];
  if (eventSeriesProperty && eventSeriesProperty.ID > 0) {
    ticketQueries.push({
      queryKey: ['tickets-series', String(eventSeriesProperty.ID)],
      queryFn: () => getEventSeriesTicketDetails(eventSeriesProperty.ID),
      enabled: eventSeriesProperty.ID > 0,
    });
  }
  if (eventProperty && eventProperty.ID > 0) {
    ticketQueries.push({
      queryKey: ['tickets', String(eventProperty.ID)],
      queryFn: () => getEventTicketDetails(eventProperty.ID),
      enabled: eventProperty.ID > 0,
    });
  }
  const ticketsData = useQueries({ queries: ticketQueries });
  const ticketsAreLoading = ticketsData.some(
    query => query.status === 'pending'
  );
  const ticketsReturnedError = ticketsData.some(
    query => query.status === 'error'
  );

  const tickets = ticketsData.flatMap(query => query.data).filter(Boolean) as
    | ITicket[]
    | ITicketSummary[];
  const { data: broker } = useQuery({
    queryKey: ['broker', event.ID],
    queryFn: () => getBrokerDetails(event),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  });

  const { data: talents } = useQuery({
    queryKey: ['talents', event.ID],
    queryFn: () => getTalentDetails(event),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  });
  const { data: venue } = useQuery({
    queryKey: ['venue', event.ID],
    queryFn: () => getVenueDetails(event),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  });
  const { data: questions } = useQuery({
    queryKey: ['questions', event.ID],
    queryFn: () => getQuestions(event.ID),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  });

  const [events, setEvents] = useState<IEvent[]>();

  const repIdParameter = repId ? `?repId=${repId}` : '';
  const cheapestTicket =
    tickets && tickets.sort((a, b) => a.SalePrice - b.SalePrice)[0];
  const sortedTalents = talents && talents.sort((a, b) => a.Order - b.Order);

  // Reset ticket state on entering the page
  useEffect(
    withErrorBoundary(() => {
      resetContext();
    }),
    []
  );

  useEffect(() => {
    if (eventProperty) {
      setEvent(eventProperty);
    }
    if (eventSeriesProperty) {
      setEventSeries(eventSeriesProperty);
    }
    if (eventsProperty && eventsProperty.length > 0) {
      setEvents(eventsProperty);
    }
  }, [eventProperty, eventsProperty, eventSeriesProperty]);

  useEffect(() => {
    if (questions) {
      setEventQuestions(questions);
    }
  }, [questions]);

  useEffect(() => {
    const configs: InitTrackingParameters[] = [
      {
        level: 'platform',
        trackingIds: {
          TrackingGoogleID:
            process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TRACKING_ID,
          TrackingMetaID: process.env.NEXT_PUBLIC_META_TRACKING_ID,
        },
      },
    ];
    if (event.ID !== 0 && event.Organisation) {
      configs.push({
        level: 'organisation',
        trackingIds: {
          TrackingGoogleID: event.Organisation.TrackingGoogleID,
          TrackingMetaID: event.Organisation.TrackingMetaID,
        },
      });
    }
    if (broker) {
      configs.push({
        level: 'broker',
        trackingIds: {
          TrackingGoogleID: broker.TrackingGoogleID,
          TrackingMetaID: broker.TrackingMetaID,
        },
      });
    }
    tracker.init(configs);
  }, [event, broker]);

  const checkoutStepUrl = eventSeries
    ? `/event/${event.ID}/checkout/date-select${repIdParameter}`
    : tickets?.some(ticket => ticket.HasTimeslot)
    ? `/event/${event.ID}/checkout/timeslot-select${repIdParameter}`
    : `/event/${event.ID}/checkout${repIdParameter}`;

  const availableTickets = tickets
    ? tickets.filter(ticket => {
        return ticket.Available !== false;
      })
    : [];

  const handleClick = () => {
    tracker.track({
      event: {
        event: 'Checkout Flow Started',
        category: 'Ticket Selection',
        value: eventSeries?.ID ?? event.ID,
      },
      platforms: ['ga'],
    });
    push(checkoutStepUrl);
  };

  const renderCtaContainer = () => {
    return ticketsAreLoading ? (
      <CtaContainer buttonText='Loading...' href={`/event/${event.ID}`}>
        <div className='ctaText'>
          <p style={{ opacity: 0 }}>only with Scope Tickets</p>
        </div>
      </CtaContainer>
    ) : ticketsReturnedError ? (
      <CtaContainer buttonText='No Tickets Available.'>
        <div className='ctaText'>
          <p>Failed to load tickets.</p>
        </div>
      </CtaContainer>
    ) : availableTickets && availableTickets.length > 0 ? (
      <CtaContainer buttonText='Get Tickets' onButtonClick={handleClick}>
        <div className='ctaText'>
          <h3>
            {cheapestTicket
              ? cheapestTicket.SalePrice > 0
                ? `From ${currencyLookup(cheapestTicket.Currency)}${(
                    cheapestTicket.SalePrice / 100
                  ).toFixed(2)}`
                : 'Free'
              : 'Wait List'}
          </h3>
          <p>only with {broker?.Name || 'Scope Tickets'}</p>
        </div>
      </CtaContainer>
    ) : (
      <CtaContainer
        buttonText='Join Waiting List'
        href={`/event/${event.ID}/waiting`}
      >
        <div className='ctaText'>
          <p>No Tickets Available.</p>
        </div>
      </CtaContainer>
    );
  };

  // FIXME causes initial black flash
  if (!event.ID) {
    return <></>;
  }

  return (
    <EventWrapper event={event} eventSeries={eventSeries}>
      {event.Date ? (
        <div className='eventInformation'>
          <section className='eventTitleContainer widthInner'>
            <h1>{event.Name}</h1>
            <h3 className='widthInner'>
              {event.EventSeriesID
                ? ''
                : dayjs(event.Date).format(DATE_FORMAT_SHORT) + ' • '}
              {event.VenueName}
            </h3>
          </section>

          <section className='eventParameters'>
            {!event.EventSeriesID && (
              <div
                className='eventInformationCard'
                id='eventInformationDateAndTime'
              >
                <div className='eventInformationIcon'>
                  <svg
                    id='iconEventDateAndTime'
                    data-name='iconEventDateAndTime'
                    viewBox='0 0 100 100'
                  >
                    <path d='m80,5h-6v-2c0-1.65685-1.34315-3-3-3h-4c-1.65685,0-3,1.34315-3,3v2h-28.07093v-2c0-1.65685-1.34315-3-3-3h-4c-1.65685,0-3,1.34315-3,3v2h-5.92907C8.95431,5,0,13.95431,0,25v55c0,11.04569,8.95431,20,20,20h60c11.04569,0,20-8.95431,20-20V25c0-11.04569-8.95431-20-20-20Zm10,75.00018c0,5.52285-4.47715,10-10,10H20c-5.52285,0-10-4.47715-10-10v-38.67615h80v38.67615ZM10,31.32404v-6.32385c0-5.52285,4.47715-10,10-10h5.92907v1.99982c0,1.65685,1.34315,3,3,3h4c1.65685,0,3-1.34315,3-3v-1.99982h28.07093v1.99982c0,1.65685,1.34315,3,3,3h4c1.65685,0,3-1.34315,3-3v-1.99982h6c5.52285,0,10,4.47715,10,10v6.32385H10Z' />
                    <rect
                      x='20'
                      y='70'
                      width='13.33333'
                      height='10'
                      rx='2'
                      ry='2'
                    />
                    <rect
                      x='43.33333'
                      y='70'
                      width='13.33333'
                      height='10'
                      rx='2'
                      ry='2'
                    />
                    <rect
                      x='66.66667'
                      y='70'
                      width='13.33333'
                      height='10'
                      rx='2'
                      ry='2'
                    />
                    <rect
                      x='20'
                      y='51.32404'
                      width='13.33333'
                      height='10'
                      rx='2'
                      ry='2'
                    />
                    <rect
                      x='43.33333'
                      y='51.32404'
                      width='13.33333'
                      height='10'
                      rx='2'
                      ry='2'
                    />
                    <rect
                      x='66.66667'
                      y='51.32404'
                      width='13.33333'
                      height='10'
                      rx='2'
                      ry='2'
                    />
                  </svg>
                </div>
                <div className='eventInformationText'>
                  <h2>
                    {`${timeFormatter(
                      new Date(new Date(event.LocalStartTime).getTime())
                    )} - ${timeFormatter(
                      new Date(new Date(event.LocalEndTime).getTime())
                    )}`}
                  </h2>
                  <h3>{dayjs(event.Date).format(DATE_FORMAT_LONG)}</h3>
                </div>
              </div>
            )}
            {venue && (
              <div className='eventInformationCard' id='eventInformationVenue'>
                <div className='eventInformationIcon'>
                  <svg
                    id='iconEventLocation'
                    data-name='iconEventLocation'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 100 100'
                  >
                    <path d='m70.00006,20C70.00006,8.95434,61.04547,0,50.00006,0s-20,8.95435-20,20c0,9.37891,6.46484,17.22766,15.17529,19.38953l.57471,33.07092h8.5l.74854-33.1156c8.62207-2.22278,15.00146-10.02844,15.00146-19.34485Zm-20.00049,9.99976c-5.51367,0-10-4.48633-10-10s4.48633-10,10-10,10,4.48633,10,10-4.48633,10-10,10Z' />
                    <path d='m99.02838,77.682l-8.83143-20.59154c-3.1528-7.35113-10.38227-12.11683-18.38097-12.11683h-5.16846c-1.10457,0-2,.89543-2,2v5.99976c0,1.10457.89543,2,2,2h5.16864c3.99927,0,7.61393,2.38284,9.19025,6.05835l8.83143,20.5923c.10671.24882.16174.51673.16174.78746v2.53552c0,2.76142-2.23858,5-5,5H14.99957c-2.76142,0-5-2.23858-5-5v-2.53552c0-.27073.05503-.53864.16174-.78746l8.83136-20.59214c1.57636-3.67562,5.19112-6.05852,9.1905-6.05852h5.26221c1.10457,0,2-.89543,2-2v-5.99976c0-1.10457-.89543-2-2-2h-5.26151c-7.99853,0-15.22785,4.7656-18.38058,12.11658L.97174,77.682C.33065,79.17679.00006,80.78629.00006,82.41276v2.5345C.00006,93.23153,6.71579,99.94726,15.00006,99.94726h70c8.28427,0,15-6.71573,15-15v-2.5345c0-1.62647-.33058-3.23596-.97168-4.73075Z' />
                  </svg>
                </div>
                <div className='eventInformationText'>
                  <h2>{venue.Name}</h2>
                  <h3>
                    {venue.Street}, {venue.City}
                  </h3>
                </div>
              </div>
            )}
            <div className='customEventInformationContainer'>
              {event.EventInformation?.map((info: IEventInformation) => (
                <div className='eventInformationCard' key={info.ID}>
                  <div className='eventInformationIcon'>
                    {/** TODO: remove legacy emoji symbol support */}
                    {info.Symbol[0] === ':' ? (
                      <img
                        src={
                          emojiParser(info.Symbol.split(':')[1]) ||
                          emojiParser('cd')
                        }
                        alt='Information Icon'
                        style={{ height: 'inherit' }}
                      />
                    ) : (
                      info.Symbol
                    )}
                  </div>
                  <div className='eventInformationText'>
                    <p>{parse(info.Content)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div
              className='eventInformationCard'
              id='eventBookingFeeRefundDisclaimer'
            >
              <div className='eventInformationIcon'>
                <svg
                  id='iconRefundDisclaimer'
                  data-name='iconRefundDisclaimer'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 100 100'
                >
                  <path d='m50.00008,0C22.38583,0,.00008,22.38574.00008,50s22.38574,50.00006,50,50.00006,50-22.3858,50-50.00006S77.61434,0,50.00008,0Zm.00024,90c-22.05566,0-40-17.94434-40-40S27.94466,10,50.00033,10s40,17.94434,40,40-17.94434,40-40,40Z' />
                  <path d='m69.41638,62.34918c-5.87915-5.50171-8.97437-8.39825-13.19641-12.34918,4.22205-3.95093,7.31726-6.84747,13.19641-12.34924l-.00928-.00928c.97498-.91241,1.5929-2.20087,1.5929-3.64148,0-2.76141-2.23853-5-5-5-1.44067,0-2.729.61792-3.64148,1.59283l-.00928-.00928c-5.50183,5.87921-8.39832,8.97443-12.34924,13.19647-3.95093-4.22205-6.84741-7.31726-12.34924-13.19647l-.00928.00928c-.91248-.97491-2.20081-1.59283-3.64148-1.59283-2.76147,0-5,2.23859-5,5,0,1.44061.61792,2.72906,1.5929,3.64148l-.00928.00928c5.87915,5.50177,8.97437,8.39832,13.19641,12.34924-4.22205,3.95093-7.31726,6.84747-13.19641,12.34918l.0094.00934c-.97498.91241-1.59302,2.20087-1.59302,3.64148,0,2.76141,2.23853,5,5,5,1.44067,0,2.729-.61798,3.64148-1.5929l.00928.00928c5.50183-5.87921,8.39832-8.97443,12.34924-13.19641,3.95093,4.22198,6.84741,7.3172,12.34924,13.19641l.00928-.00928c.91248.97491,2.20081,1.5929,3.64148,1.5929,2.76147,0,5-2.23859,5-5,0-1.44061-.61804-2.72906-1.59302-3.64148l.0094-.00934Z' />
                </svg>
              </div>
              <div className='eventInformationText'>
                <h4>Please note that service fees are non-refundable</h4>
              </div>
            </div>
          </section>

          {sortedTalents && sortedTalents.length > 0 && (
            <div className='eventLineupContainer'>
              <hr></hr>
              <h1 className='widthInner'>Featuring</h1>
              <div className='LineupItemWrapper'>
                <TalentImageRow talentDetails={talents} />
              </div>
            </div>
          )}

          {event.Description && (
            <div className='eventDescriptionContainer'>
              <hr></hr>
              <h1 className='widthInner'>Event Description</h1>
              <div className='widthInner'>
                <LineRenderer lines={event.Description.split('\n')} />
              </div>
            </div>
          )}

          <hr></hr>

          <h1 className='widthInner'>Event Organiser</h1>

          <section className='eventOrganiser'>
            <div className='eventInformationCard'>
              {event.Organisation?.Logo && (
                <div className='organiserLogo'>
                  <img src={event.Organisation.Logo} alt='Organiser Logo' />
                </div>
              )}
              <div className='organiserName'>
                <h4>Event Organiser</h4>
                <h2>{event.Organisation?.Name}</h2>
              </div>
            </div>
            {broker && !broker.IsHidden && (
              <div className='eventInformationCard broker'>
                <div className='brokerText'>
                  <p>Ticketing Partner </p>
                  <a
                    href={broker.Link}
                    className='brokerContainer'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <p className='brokerName'>{broker.Name}</p>
                  </a>
                </div>
                <div className='brokerLogo'>
                  <img src={broker.Logo} alt='Broker logo' />
                </div>
              </div>
            )}
          </section>

          <hr></hr>

          {Date.now() < Date.parse(event.Ends) ||
          events?.some(
            event => event.Ends && Date.now() < Date.parse(event.Ends)
          ) ? (
            renderCtaContainer()
          ) : (
            <div className='ctaContainer'>
              <div className='eventIsOver'>
                <p>This Event Has Ended</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          This event is currently not available. Please try again later.
        </div>
      )}
    </EventWrapper>
  );
}

export const getServerSideProps: GetServerSideProps = async context =>
  loadServerSideData(context);

export default function EventLandingPageRoute({
  dehydratedState,
  event,
  events,
  eventSeries,
  venue,
  tickets,
  talents,
}: {
  dehydratedState: DehydratedState;
  event: IEvent;
  events: IEvent[];
  eventSeries?: IEventSeries;
  venue?: IVenue;
  tickets?: (ITicket | ITicketSummary)[];
  talents?: ITalent[];
}) {
  return (
    <QueryClientProvider client={sharedQueryClient}>
      {/* Rendered from the props rather than from context, so that it survives
          the server render that crawlers actually see. */}
      <EventMeta
        event={event}
        events={events}
        eventSeries={eventSeries}
        venue={venue}
        tickets={tickets}
        talents={talents}
      />
      <HydrationBoundary state={dehydratedState}>
        <EventLandingPage
          event={event}
          events={events}
          eventSeries={eventSeries}
        />
      </HydrationBoundary>
    </QueryClientProvider>
  );
}

const LineRenderer = (properties: { lines: string[] }) => {
  try {
    return (
      <>
        {properties.lines.map((line, index) => (
          <p key={`line-${index}`}>
            {parse(line, {
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              replace() {},
            })}
          </p>
        ))}
      </>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};
